import { Check } from "lucide-react";
import { cn } from "../../../lib/utils";
import { buildOrderTimeline, formatStepDateTime } from "../lib/orderDetailsHelpers";

// Ported verbatim from client/src/features/orders/components/OrderProgressBar.jsx
// (the seller-portal's own public tracking page) — same steps, same fill
// logic, so a tracking link on this storefront's own domain never disagrees
// with the shared app.reevoxi.com one for the same order.
//
// Reads order.status only (never the raw Dropit/Shiprocket shipment status)
// — that's the whole point of routing every carrier's webhook through the
// same seller-portal status vocabulary (see dropit-webhook.service.ts's
// ORDER_STATUS_FOR_DROPIT).
//
// `steps` is the seller's own configured forward-chain statuses (DB-driven),
// already ordered and already excluding the terminal side-states (Cancelled,
// Returned).
//
// Takes the full order (not just its status string) so each reached step can
// show the date/time it was actually reached.
//
// A genuinely Cancelled order gets an extra final "Cancelled" column
// appended (in red), with its own cancellation date/time — never shown for
// any other order, including Returned.
export default function OrderProgressBar({ order, steps, cancelledLabel }) {
  const stepKeys = steps.map((s) => s.key);
  const timeline = buildOrderTimeline(order, stepKeys);
  const isCancelled = order.status === "Cancelled";
  const displaySteps = isCancelled
    ? [...steps, { key: "Cancelled", label: cancelledLabel || "Cancelled" }]
    : steps;

  return (
    <div className="flex items-start">
      {displaySteps.map(({ key: step, label }, i) => {
        const stepTimeline = timeline[i];
        const isDone = !!stepTimeline?.done;
        const isCurrent = stepTimeline?.state === "current" || stepTimeline?.state === "cancelled";
        const isCancelledStep = step === "Cancelled";
        const nextDone = !!timeline[i + 1]?.done;
        const isFirst = i === 0;
        const isLast = i === displaySteps.length - 1;
        const doneLineClass = isCancelledStep ? "bg-red-500" : "bg-brand-500";
        const doneDotClass = isCancelledStep ? "border-red-500 bg-red-500" : "border-brand-500 bg-brand-500";
        const doneLabelClass = isCancelledStep ? "text-red-700" : "text-brand-700";
        return (
          <div key={step} className="flex min-w-0 flex-1 flex-col items-center">
            <div className="flex w-full items-center">
              <div className={cn("h-0.5 flex-1", isFirst ? "invisible" : isDone ? doneLineClass : "bg-gray-200")} />
              <div
                className={cn(
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                  isDone ? doneDotClass : "border-gray-300 bg-white",
                  isCurrent && (isCancelledStep ? "ring-4 ring-red-100" : "ring-4 ring-brand-100")
                )}
              >
                {isDone && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
              </div>
              <div className={cn("h-0.5 flex-1", isLast ? "invisible" : nextDone ? doneLineClass : "bg-gray-200")} />
            </div>
            <span
              className={cn(
                "mt-1.5 px-0.5 text-center text-[10px] font-semibold leading-tight",
                isDone ? doneLabelClass : "text-gray-400"
              )}
            >
              {label}
            </span>
            {stepTimeline?.state !== "pending" && (
              <span className="px-0.5 text-center text-[9px] leading-tight text-gray-400">
                {formatStepDateTime(stepTimeline.timestamp)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
